import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import {
  parseReferralBonusTab,
  REFERRAL_BONUS_TAB_QUERY_PARAM,
  ReferralBonusTab,
} from '../features/referral-bonus/model/referral-bonus-tab';
import { ReferralBonusHeader } from '../features/referral-bonus/ui/referral-bonus-header';
import { ReferralBonusHeroCard } from '../features/referral-bonus/ui/referral-bonus-hero-card';
import { ReferralBonusInviteButton } from '../features/referral-bonus/ui/referral-bonus-invite-button';
import { ReferralBonusParticipantCard } from '../features/referral-bonus/ui/referral-bonus-participant-card';
import { ReferralBonusProgressTabs } from '../features/referral-bonus/ui/referral-bonus-progress-tabs';
import { ReferralBonusQrSheet } from '../features/referral-bonus/ui/referral-bonus-qr-sheet';
import {
  getEmployeeReferrals,
  getEmployeeReferralsCount,
} from '../shared/api/employee.service';
import { useEmployeeReferralLinkActions } from '../shared/hooks/use-employee-referral-link-actions';
import { useEmployeeSession } from '../shared/hooks/use-employee-session';
import { BottomNavPageLayout, HomeDashboardBottomNav } from '../shared/ui';
import { ReferralProgressCard } from '../shared/ui/referral-progress-card';

const FRAME_TOP_PADDING_PX = 20;
const FRAME_SIDE_PADDING_PX = 29;
const FOOTER_TOP_PADDING_PX = 12;
const FOOTER_BOTTOM_PADDING_PX = 13;
const INVITE_FEEDBACK_DURATION_MS = 2200;
const REFERRALS_PAGE_SIZE = 20;

const ContentLayout = styled.div.attrs({
  'data-ui': 'referral-page-content',
})`
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  row-gap: ${({ theme }) => theme.scale.px(12)};
  padding-top: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_TOP_PADDING_PX)}, env(safe-area-inset-top))`};
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(
      FRAME_SIDE_PADDING_PX,
    )}, env(safe-area-inset-right))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_SIDE_PADDING_PX)}, env(safe-area-inset-left))`};
`;

const ScrollableContent = styled.div.attrs({
  'data-ui': 'referral-page-scroll-content',
})`
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  display: grid;
  align-content: start;
  gap: ${({ theme }) => theme.scale.px(10)};
`;

const TabsSlot = styled.div.attrs({
  'data-ui': 'referral-page-tabs-slot',
})`
  display: grid;
  justify-items: center;
  margin-top: ${({ theme }) => theme.scale.px(6)};
  margin-bottom: ${({ theme }) => theme.scale.px(10)};
`;

const BottomNavSlot = styled.div.attrs({
  'data-ui': 'referral-page-bottom-nav-slot',
})`
  flex-shrink: 0;
  display: grid;
  justify-items: center;
  padding-top: ${({ theme }) => theme.scale.px(FOOTER_TOP_PADDING_PX)};
  padding-right: ${({ theme }) =>
    `max(${theme.scale.px(
      FRAME_SIDE_PADDING_PX,
    )}, env(safe-area-inset-right))`};
  padding-bottom: ${({ theme }) =>
    `max(${theme.scale.px(
      FOOTER_BOTTOM_PADDING_PX,
    )}, env(safe-area-inset-bottom))`};
  padding-left: ${({ theme }) =>
    `max(${theme.scale.px(FRAME_SIDE_PADDING_PX)}, env(safe-area-inset-left))`};
`;

export function ReferralPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { referralLink, shareReferralLink } = useEmployeeReferralLinkActions();
  const { status } = useEmployeeSession();
  const inviteFeedbackTimerRef = useRef<number | null>(null);
  const [inviteFeedbackText, setInviteFeedbackText] = useState<string | null>(
    null,
  );
  const [isQrSheetOpen, setIsQrSheetOpen] = useState(false);
  const activeTab = parseReferralBonusTab(
    searchParams.get(REFERRAL_BONUS_TAB_QUERY_PARAM),
  );
  const referralsCountQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: getEmployeeReferralsCount,
    queryKey: ['employee', 'referrals', 'count'],
  });
  const referralsQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: () =>
      getEmployeeReferrals({
        isConverted: activeTab === 'completed',
        limit: REFERRALS_PAGE_SIZE,
      }),
    queryKey: ['employee', 'referrals', activeTab, REFERRALS_PAGE_SIZE],
  });

  const setTab = (tab: ReferralBonusTab) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    if (tab === 'in-progress') {
      nextSearchParams.delete(REFERRAL_BONUS_TAB_QUERY_PARAM);
    } else {
      nextSearchParams.set(REFERRAL_BONUS_TAB_QUERY_PARAM, 'completed');
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  useEffect(() => {
    return () => {
      if (inviteFeedbackTimerRef.current !== null) {
        window.clearTimeout(inviteFeedbackTimerRef.current);
      }
    };
  }, []);

  const showInviteFeedback = (nextText: string) => {
    setInviteFeedbackText(nextText);

    if (inviteFeedbackTimerRef.current !== null) {
      window.clearTimeout(inviteFeedbackTimerRef.current);
    }

    inviteFeedbackTimerRef.current = window.setTimeout(() => {
      setInviteFeedbackText(null);
      inviteFeedbackTimerRef.current = null;
    }, INVITE_FEEDBACK_DURATION_MS);
  };

  const openQrSheet = () => {
    setIsQrSheetOpen(true);
  };

  const closeQrSheet = () => {
    setIsQrSheetOpen(false);
  };

  const handleShareFromSheet = async () => {
    const result = await shareReferralLink();

    if (result === 'copied') {
      showInviteFeedback(t('referralBonus.copySuccessToast'));
    }

    if (result === 'shared' || result === 'copied') {
      closeQrSheet();
    }
  };

  const referralsCount = referralsCountQuery.data;
  const participants = (referralsQuery.data?.items ?? []).map(participant => ({
    firstName: participant.firstName,
    id: participant.id,
    lastName: participant.lastName,
    statusKind: participant.isConverted ? 'completed' : 'pending',
  }));
  const emptyStateText =
    activeTab === 'completed'
      ? t('referralBonus.emptyCompleted')
      : t('referralBonus.emptyInProgress');

  return (
    <BottomNavPageLayout
      frameDataUi="referral-page-frame"
      overlay={
        <ReferralBonusQrSheet
          isOpen={isQrSheetOpen}
          onClose={closeQrSheet}
          onShare={handleShareFromSheet}
          referralLink={referralLink}
        />
      }
      pageDataUi="referral-page"
      stickyFooter={
        <BottomNavSlot>
          <HomeDashboardBottomNav
            activeTab={null}
            onOpenHome={() => {
              navigate('/home');
            }}
            onOpenMyPage={() => {
              navigate('/my-page');
            }}
          />
        </BottomNavSlot>
      }
    >
      <ContentLayout>
        <ReferralBonusHeader
          onBack={() => {
            navigate('/home');
          }}
          onOpenQr={referralLink ? openQrSheet : undefined}
        />
        <ScrollableContent>
          <ReferralBonusHeroCard
            onOpenRaffle={() => {
              navigate('/referral/raffle/professional');
            }}
          />
          <ReferralBonusInviteButton
            disabled={!referralLink}
            feedbackText={inviteFeedbackText}
            onInvite={() => {
              openQrSheet();
            }}
          />
          <ReferralProgressCard
            chancePercent={referralsCount?.chancePercent ?? 0}
            referralsCount={referralsCount?.total ?? 0}
            ticketsCount={referralsCount?.ticketsCount ?? 0}
          />
          <TabsSlot>
            <ReferralBonusProgressTabs
              activeTab={activeTab}
              onChange={setTab}
            />
          </TabsSlot>
          <ReferralBonusParticipantCard
            emptyStateText={emptyStateText}
            isError={referralsQuery.isError}
            participants={participants}
          />
        </ScrollableContent>
      </ContentLayout>
    </BottomNavPageLayout>
  );
}
